// src/pages/AdminSystemLogs.js
//
// App Settings → System Logs sub-tab.
//
// Two sections:
//   1. Event toggles — enable/disable each audit event type
//   2. Log viewer — filterable by tournament and event type

import React, { useEffect, useState, useCallback } from 'react';
import { getAppConfig, updateAppConfig, getAuditLog, getMyAuditLog, getTournaments, getOrganizers } from '../services/api';

// ---------------------------------------------------------------------------
// Event type definitions — label shown in UI, key in audit_events JSON
// ---------------------------------------------------------------------------
const EVENT_DEFS = [
    { key: 'pick_submitted',                 label: 'Pick submitted / updated' },
    { key: 'game_result_recorded',           label: 'Game result recorded' },
    { key: 'registration_approved_rejected', label: 'Registration approved or rejected' },
    { key: 'password_reset',                 label: 'Admin resets participant password' },
    { key: 'participant_name_edited',        label: 'Admin edits participant name' },
    { key: 'password_changed',               label: 'Participant changes own password' },
    { key: 'pool_created_deleted',           label: 'Pool created or deleted' },
    { key: 'tournament_created_deleted',     label: 'Tournament created or deleted' },
    { key: 'danger_zone_actions',            label: 'Danger Zone actions' },
    { key: 'organizer_status_changed',       label: 'Organizer status changed' },
];

// Human-friendly event type labels for the log viewer.
const EVENT_LABELS = Object.fromEntries(EVENT_DEFS.map(e => [e.key, e.label]));

// ---------------------------------------------------------------------------
// Toggle switch component
// ---------------------------------------------------------------------------
function Toggle({ checked, onChange, label }) {
    return (
        <label className="flex items-center justify-between py-3 cursor-pointer group">
            <span className="text-sm text-white group-hover:text-orange-300 transition-colors">
                {label}
            </span>
            <div
                onClick={onChange}
                className={
                    'relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ' +
                    (checked ? 'bg-orange-500' : 'bg-gray-700')
                }>
                <div className={
                    'absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ' +
                    (checked ? 'translate-x-5' : 'translate-x-0.5')
                } />
            </div>
        </label>
    );
}

// ---------------------------------------------------------------------------
// Detail cell — renders JSONB detail nicely. Any `<x>_id` key with a
// sibling `<x>_name` key (e.g. entered_by_participant_id /
// entered_by_participant_name, added so a synced/corrected result shows
// who actually clicked it, not just their id) gets merged into one
// entry -- id with the name right beside it -- rather than showing the
// name as its own separate, redundant-looking trailing entry.
function DetailCell({ detail }) {
    if (!detail) return <span className="text-gray-600">—</span>;
    const nameKeys = new Set(Object.keys(detail).filter(k => k.endsWith('_name') && detail[k.replace(/_name$/, '_id')] !== undefined));
    const entries = Object.entries(detail).filter(([k]) => !nameKeys.has(k));
    return (
        <span className="text-xs text-gray-400 font-mono">
            {entries.map(([k, v]) => {
                const nameKey = `${k.replace(/_id$/, '')}_name`;
                const name = k.endsWith('_id') ? detail[nameKey] : undefined;
                return name ? `${k}: ${v} (${name})` : `${k}: ${v}`;
            }).join(' · ')}
        </span>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
function AdminSystemLogs({ showFeedback, activeTournament, user = {} }) {
    // Deliberately is_platform_admin ONLY -- see the identical comment in
    // AdminOrganizers.js. The backend's platformAdminOnly middleware for
    // both /audit-log and /admin/organizers checks is_platform_admin
    // exclusively, never the legacy is_admin flag.
    const isPlatformAdmin = user.is_platform_admin;

    // An organizer (not platform admin) with active access gets their own
    // scoped view instead of the flat "not available" message -- see
    // GET /audit-log/mine (src/routes/audit-log.js), added specifically
    // for this. organizer_id comes from GET /auth/me's participant
    // payload.
    const hasOrganizerAccess = !isPlatformAdmin && !!user.organizer_id;

    // All hooks must be called unconditionally before any early return.
    const [toggles,          setToggles]          = useState({});
    const [loadingCfg,       setLoadingCfg]       = useState(true);
    const [savingCfg,        setSavingCfg]        = useState(false);
    const [savedMsg,         setSavedMsg]         = useState('');
    const [logs,             setLogs]             = useState([]);
    const [loadingLogs,      setLoadingLogs]      = useState(true);
    const [tournaments,      setTournaments]      = useState([]);
    const [organizers,       setOrganizers]        = useState([]);
    const [filterTournament, setFilterTournament] = useState('');
    const [filterOrganizer,  setFilterOrganizer]  = useState('');
    const [filterEventType,  setFilterEventType]  = useState('');

    // Load toggles from app_config — only fires for platform admins.
    useEffect(() => {
        if (!isPlatformAdmin) { setLoadingCfg(false); return; }
        getAppConfig()
            .then(res => {
                let parsed = {};
                try { parsed = JSON.parse(res.data.audit_events || '{}'); } catch {}
                const merged = {};
                for (const { key } of EVENT_DEFS) {
                    merged[key] = parsed[key] !== false;
                }
                setToggles(merged);
            })
            .catch(() => showFeedback('Failed to load settings', 'error'))
            .finally(() => setLoadingCfg(false));
    }, [isPlatformAdmin, showFeedback]);

    // Load tournaments for filter dropdown. Fires for platform admins
    // (sees all) AND organizers with active access (GET /tournaments
    // already scopes to just their own tournaments server-side for
    // non-admins -- no client-side filtering needed here).
    useEffect(() => {
        if (!isPlatformAdmin && !hasOrganizerAccess) return;
        getTournaments()
            .then(res => setTournaments(res.data))
            .catch(() => {});
        if (activeTournament) setFilterTournament(String(activeTournament.id));
    }, [isPlatformAdmin, hasOrganizerAccess, activeTournament]);

    // Load organizers for filter dropdown.
    useEffect(() => {
        if (!isPlatformAdmin) return;
        getOrganizers()
            .then(res => setOrganizers(res.data))
            .catch(() => {});
    }, [isPlatformAdmin]);

    // Fetch logs.
    const fetchLogs = useCallback(() => {
        if (!isPlatformAdmin && !hasOrganizerAccess) return;
        setLoadingLogs(true);
        const params = {};
        if (filterTournament) params.tournament_id = filterTournament;
        if (filterEventType)  params.event_type    = filterEventType;

        const request = isPlatformAdmin
            ? (() => { if (filterOrganizer) params.organizer_id = filterOrganizer; return getAuditLog(params); })()
            : getMyAuditLog(params);

        request
            .then(res => setLogs(res.data))
            .catch(() => showFeedback('Failed to load audit log', 'error'))
            .finally(() => setLoadingLogs(false));
    }, [isPlatformAdmin, hasOrganizerAccess, filterTournament, filterOrganizer, filterEventType, showFeedback]);

    useEffect(() => { fetchLogs(); }, [fetchLogs]);

    const handleToggle = (key) => {
        setToggles(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleSaveToggles = async () => {
        setSavingCfg(true);
        setSavedMsg('');
        try {
            await updateAppConfig({ audit_events: JSON.stringify(toggles) });
            setSavedMsg('Saved');
            setTimeout(() => setSavedMsg(''), 2000);
        } catch {
            showFeedback('Failed to save settings', 'error');
        } finally {
            setSavingCfg(false);
        }
    };

    // Early return AFTER all hooks.
    if (!isPlatformAdmin && !hasOrganizerAccess) {
        return (
            <div className="max-w-lg">
                <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 text-center">
                    <p className="text-gray-400 text-sm">
                        System logs are only available to organizers and platform administrators.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl">
            <h3 className="text-white font-bold text-lg mb-1">
                {isPlatformAdmin ? 'System Logs' : 'My Activity Log'}
            </h3>
            <p className="text-gray-500 text-sm mb-6">
                {isPlatformAdmin
                    ? 'Configure which events are recorded and review the audit trail.'
                    : 'A record of activity on your account and tournaments — including any time platform staff accessed your account for support.'}
            </p>

            {/* Event toggles -- platform-wide config, platform admin only */}
            {isPlatformAdmin && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-6">
                <div className="flex items-center justify-between mb-3">
                    <p className="text-white font-bold text-sm">Logged Events</p>
                    <div className="flex items-center gap-3">
                        {savedMsg && (
                            <span className="text-green-400 text-sm font-bold">{savedMsg}</span>
                        )}
                        <button
                            onClick={handleSaveToggles}
                            disabled={savingCfg || loadingCfg}
                            className="bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-bold px-4 py-1.5 rounded-lg text-sm transition-colors">
                            {savingCfg ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </div>
                {loadingCfg ? (
                    <p className="text-gray-500 text-sm">Loading…</p>
                ) : (
                    <div className="divide-y divide-gray-800">
                        {EVENT_DEFS.map(({ key, label }) => (
                            <Toggle
                                key={key}
                                label={label}
                                checked={toggles[key] !== false}
                                onChange={() => handleToggle(key)}
                            />
                        ))}
                    </div>
                )}
            </div>
            )}

            {/* Log viewer */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-800 flex items-center gap-3 flex-wrap">
                    <p className="text-white font-bold text-sm flex-shrink-0">Audit Log</p>
                    <select
                        value={filterTournament}
                        onChange={e => setFilterTournament(e.target.value)}
                        className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-orange-500">
                        <option value="">All tournaments</option>
                        {tournaments.map(t => (
                            <option key={t.id} value={String(t.id)}>{t.name}</option>
                        ))}
                    </select>
                    <select
                        value={filterOrganizer}
                        onChange={e => setFilterOrganizer(e.target.value)}
                        className={'bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-orange-500' + (isPlatformAdmin ? '' : ' hidden')}>
                        <option value="">All organizers</option>
                        {organizers.map(o => (
                            <option key={o.id} value={String(o.id)}>{o.display_name}</option>
                        ))}
                    </select>
                    <select
                        value={filterEventType}
                        onChange={e => setFilterEventType(e.target.value)}
                        className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-orange-500">
                        <option value="">All event types</option>
                        {EVENT_DEFS.map(({ key, label }) => (
                            <option key={key} value={key}>{label}</option>
                        ))}
                    </select>
                    <button
                        onClick={fetchLogs}
                        className="text-xs text-gray-500 hover:text-orange-400 transition-colors ml-auto">
                        Refresh ↻
                    </button>
                </div>

                {loadingLogs ? (
                    <div className="px-4 py-6 text-gray-500 text-sm text-center">Loading…</div>
                ) : logs.length === 0 ? (
                    <div className="px-4 py-6 text-gray-500 text-sm text-center">No log entries found.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm table-fixed">
                            <colgroup>
                                <col className="w-[11%]" />
                                <col className="w-[14%]" />
                                <col className="w-[11%]" />
                                <col className="w-[11%]" />
                                <col className="w-[11%]" />
                                <col className="w-[42%]" />
                            </colgroup>
                            <thead>
                                <tr className="border-b border-gray-800">
                                    <th className="text-left px-4 py-2 text-xs text-gray-500 font-bold uppercase tracking-wide whitespace-nowrap">Time</th>
                                    <th className="text-left px-4 py-2 text-xs text-gray-500 font-bold uppercase tracking-wide">Event</th>
                                    <th className="text-left px-4 py-2 text-xs text-gray-500 font-bold uppercase tracking-wide">Organizer</th>
                                    <th className="text-left px-4 py-2 text-xs text-gray-500 font-bold uppercase tracking-wide">Actor</th>
                                    <th className="text-left px-4 py-2 text-xs text-gray-500 font-bold uppercase tracking-wide">Target</th>
                                    <th className="text-left px-4 py-2 text-xs text-gray-500 font-bold uppercase tracking-wide">Detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((log, i) => (
                                    <tr key={log.id} className={i < logs.length - 1 ? 'border-b border-gray-800/50' : ''}>
                                        <td className="px-4 py-2.5 text-gray-500 text-xs break-words">
                                            {new Date(log.created_at).toLocaleString('en-US', {
                                                month:  'short', day: 'numeric',
                                                hour:   'numeric', minute: '2-digit',
                                            })}
                                        </td>
                                        <td className="px-4 py-2.5 break-words">
                                            <span className="text-xs font-bold text-orange-400">
                                                {EVENT_LABELS[log.event_type] || log.event_type}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5 text-white text-xs break-words">
                                            {log.organizer_name || <span className="text-gray-600">—</span>}
                                        </td>
                                        <td className="px-4 py-2.5 text-white text-xs break-words">
                                            {log.actor_name || <span className="text-gray-600">—</span>}
                                        </td>
                                        <td className="px-4 py-2.5 text-white text-xs break-words">
                                            {log.target_name || <span className="text-gray-600">—</span>}
                                        </td>
                                        <td className="px-4 py-2.5 break-words">
                                            <DetailCell detail={log.detail} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AdminSystemLogs;